import * as angular from "angular";
import { CampaignDataProvider } from "./campaign.dp";
import { CanvassResultService } from "../services/result.service";
import { ChartHelper } from "../components/result/chart.helper";
import { dummyData } from "../utils/dummy-data";
import { DashboardWidget } from "../models/dashboard-widget.model";
import { DashboardWidgetFilter } from "../models/dashboard-widget-filter.model";

export class ResultDataProvider {
    public dummy = dummyData.canvassResults;
    public categories;
    public showDummy = false;
    public chartHelper: ChartHelper;
    public canvassResultService: CanvassResultService;
    public $q: any;
    public dismissedDummy;
    public overviewData = {};
    public campaignDataProvider: CampaignDataProvider;
    private $route: any;
    private $cookies: any;
    private $http: any;
    private __env: any;

    constructor(
        $q: any,
        $http: any,
        $cookies: any,
        $route: any,
        __env: any,
        CanvassResultService: CanvassResultService,
        ChartHelper: ChartHelper,
        CampaignDataProvider: CampaignDataProvider
    ) {
        this.canvassResultService = CanvassResultService;
        this.$q = $q;
        this.$http = $http;
        this.$route = $route;
        this.$cookies = $cookies;
        this.__env = __env;
        this.chartHelper = ChartHelper;
        this.campaignDataProvider = CampaignDataProvider;
        this.dismissedDummy = this.$cookies.get("dismissedDummy") || false;
    }

    clearAll() {
        this.overviewData = {};
    }

    isDummyData(): boolean {
        return this.showDummy;
    }

    dismissDummyData() {
        this.$cookies.put("dismissedDummy", this.dismissedDummy);
    }

    startAndEndDates(dateRange) {
        return {
            startDate:
                dateRange && dateRange.selectedTemplate !== "All Time"
                    ? dateRange.dateStart
                    : null,
            endDate: dateRange && dateRange.dateEnd ? dateRange.dateEnd : null,
        };
    }

    chartData(widget: DashboardWidget, filter: DashboardWidgetFilter) {
        const deferred = this.$q.defer();

        if (widget) {
            const data: DashboardWidget = angular.copy(widget);
            data.filter = filter;

            this.canvassResultService.chartData(data).then(
                (response) => {
                    const json = response["data"];
                    if (json.success) {
                        let data = json.data;
                        let chartData = ["pie", "donut"].includes(
                            widget.style.chartType
                        )
                            ? data
                            : data[0].values;

                        this.chartHelper.calculateChartDetails(chartData);
                        deferred.resolve(data);
                    } else {
                        deferred.reject(json);
                    }
                },
                (error) => {
                    deferred.reject(error);
                }
            );
        } else {
            deferred.reject();
        }

        return deferred.promise;
    }

    statsData(widget: DashboardWidget, filter?: DashboardWidgetFilter) {
        const deferred = this.$q.defer();

        if (widget) {
            const data: DashboardWidget = angular.copy(widget);
            data.filter = filter;

            this.canvassResultService.statsData(data).then(
                (response) => {
                    const json = response["data"];
                    if (json.success) {
                        let data = json.data;
                        deferred.resolve(data);
                    } else {
                        deferred.reject(json);
                    }
                },
                (error) => {
                    deferred.reject(error);
                }
            );
        } else {
            deferred.reject();
        }
        return deferred.promise;
    }

    tableData(widget: DashboardWidget, filter?: DashboardWidgetFilter) {
        const deferred = this.$q.defer();

        if (widget) {
            const data: DashboardWidget = angular.copy(widget);
            data.filter = filter;
            this.canvassResultService.tableData(data).then(
                (response) => {
                    const json = response["data"];
                    if (json.success) {
                        let data = json.data;
                        deferred.resolve(data);
                    } else {
                        deferred.reject(json);
                    }
                },
                (error) => {
                    deferred.reject(error);
                }
            );
        } else {
            deferred.reject();
        }

        return deferred.promise;
    }

    getPerformanceDataForLevel(
        viewLevel: string,
        parentId: any,
        campaignId: any | null,
        widget: any,
        filter: any
    ): any {
        const deferred: any = this.$q.defer();

        if (widget) {
            const data: any = angular.copy(widget);
            data.viewLevel = viewLevel;
            data.parentId = parentId;
            data.campaignId = campaignId;
            data.filter = filter;
            this.canvassResultService.performanceTableData(data).then(
                (response: object): void => {
                    const json: any = response["data"];
                    if (json.success) {
                        let data: any = json.data;
                        deferred.resolve(data);
                    } else {
                        deferred.reject(json);
                    }
                },
                (error: any): void => {
                    deferred.reject(error);
                }
            );
        } else {
            deferred.reject();
        }

        return deferred.promise;
    }

    pivotTableData(
        widget: DashboardWidget,
        filter?: DashboardWidgetFilter
    ): any {
        const deferred = this.$q.defer();

        if (widget) {
            const data: DashboardWidget = angular.copy(widget);
            filter.teams = [];
            filter.users = [];
            filter.dateRange["selectedTemplate"] = "All Time";
            data.filter = filter;
            this.canvassResultService.pivotTableData(data).then(
                (response: any): void => {
                    const json = response["data"];
                    if (json.success) {
                        let data = json.data;
                        deferred.resolve(data);
                    } else {
                        deferred.reject(json);
                    }
                },
                (error) => {
                    deferred.reject(error);
                }
            );
        } else {
            deferred.reject();
        }

        return deferred.promise;
    }

    dataTableExport(widget: DashboardWidget, filter?: DashboardWidgetFilter) {
        const deferred = this.$q.defer();

        if (widget) {
            const data: DashboardWidget = angular.copy(widget);
            if (widget.style.tableType == "pivot") {
                filter.teams = [];
                filter.users = [];
                filter.dateRange["selectedTemplate"] = "All Time";
            }
            data.filter = filter;
            this.canvassResultService.dataTableExport(data).then(
                (response: any): void => {
                    const json = response["data"];
                    if (json.success) {
                        let data = json.data;
                        deferred.resolve(data);
                    } else {
                        deferred.reject(json);
                    }
                },
                (error) => {
                    deferred.reject(error);
                }
            );
        } else {
            deferred.reject();
        }

        return deferred.promise;
    }

    expandRow(
        rowId: string | number,
        widget: DashboardWidget,
        filter?: DashboardWidgetFilter
    ) {
        const deferred = this.$q.defer();

        if (widget) {
            const data: any = angular.copy(widget);
            data.filter = filter;
            data.rowId = rowId;

            this.canvassResultService.expandRow(data).then(
                (response) => {
                    const json = response["data"];
                    if (json.success) {
                        let data = json.data;
                        deferred.resolve(data);
                    } else {
                        deferred.reject(json);
                    }
                },
                (error) => {
                    deferred.reject(error);
                }
            );
        } else {
            deferred.reject();
        }

        return deferred.promise;
    }

    getCustomDateTemplates() {
        let lessThan3Day = new Date();
        let lessThan7Day = new Date();
        let lessThan30Day = new Date();
        let lessThan90Day = new Date();

        return [
            {
                name: "Last 3 Days",
                dateStart: new Date(
                    lessThan3Day.setDate(lessThan3Day.getDate() - 2)
                ),
                dateEnd: new Date(),
            },
            {
                name: "Last 7 Days",
                dateStart: new Date(
                    lessThan7Day.setDate(lessThan7Day.getDate() - 6)
                ),
                dateEnd: new Date(),
            },
            {
                name: "Last 30 Days",
                dateStart: new Date(
                    lessThan30Day.setDate(lessThan30Day.getDate() - 29)
                ),
                dateEnd: new Date(),
            },
            {
                name: "Last 90 Days",
                dateStart: new Date(
                    lessThan90Day.setDate(lessThan90Day.getDate() - 89)
                ),
                dateEnd: new Date(),
            },
            {
                name: "All Time",
                dateStart: new Date(
                    this.campaignDataProvider.activeCampaign.campaigncreationdatetime
                ),
                dateEnd: new Date(),
            },
        ];
    }
}

angular
    .module("vcDataProvider")
    .service("ResultDataProvider", ResultDataProvider);
